/**
 * ---------------------------------------------------------------------
 * Copyright (c) 2021 EclipseSource Munich
 * Licensed under MIT
 * https://github.com/eclipsesource/jsonforms-editor/blob/master/LICENSE
 * ---------------------------------------------------------------------
 */

import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { ControlProps, rankWith, scopeEndsWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";

import { FormHelperText } from "@mui/material";

import Button from "@ingka/button";
import TextArea from "@ingka/text-area";
import SSRIcon from "@ingka/ssr-icon";
import informationCircle from "@ingka/ssr-icon/paths/information-circle";
import Accordion, { AccordionItem } from "@ingka/accordion";

import ShowRuleModal from "../components/ShowRuleModal";
import { StyledLabel } from "../../../styles";

const invalidJsonMessage = "Not a valid rule JSON.";

const ruleDescription = "Define conditions and effects that can dynamically control features of the UI based on data.";

const StyledButton = styled(Button)`
	margin-top: 1rem;
	width: fit-content;
`;

const StyledDiv = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledTextArea = styled(TextArea)`
	margin-top: 1rem;
	margin-bottom: 1rem;

	textarea {
		min-height: 12rem;
	}
`;

const StyledLabelWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const StyledSSRIcon = styled(SSRIcon)`
	color: rgb(var(--colour-semantic-informative, 0, 88, 163));
`;

const Wrapper = styled.div`
	margin-bottom: 1rem;
`;

const isValidRule = (rule: any) => {
	return !rule || (rule.effect && rule.condition);
};

const RuleEditor: React.FC<ControlProps> = (props) => {
	const { data, path, handleChange, errors } = props;
	const [invalidJson, setInvalidJson] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [ruleValue, setRuleValue] = useState("");
	const [expanded, setExpanded] = React.useState(false);

	React.useEffect(() => {
		if (data) {
			setRuleValue(JSON.stringify(data, null, 2));
			setExpanded(true);
		} else {
			setRuleValue("");
			setExpanded(false);
		}
	}, [data]);

	const onSubmitRule = useCallback(() => {
		try {
			const rule = ruleValue ? JSON.parse(ruleValue) : undefined;
			if (isValidRule(rule)) {
				setInvalidJson(false);
				handleChange(path, rule);
			} else {
				setInvalidJson(true);
			}
		} catch (error) {
			setInvalidJson(true);
		}
	}, [handleChange, path, ruleValue]);

	const isValid = errors.length === 0 && !invalidJson;

	return (
		<>
			<Wrapper>
				<Accordion size="small">
					<AccordionItem
						id="ruleAccordionItem"
						onClick={() => setExpanded(!expanded)}
						title={
							<StyledLabelWrapper>
								<StyledSSRIcon paths={informationCircle} />
								<span>Rule</span>
							</StyledLabelWrapper>
						}
						open={expanded}
						subtle
					>
						<StyledDiv>
							<StyledLabel>{ruleDescription}</StyledLabel>
							<StyledButton size="small" text="Show examples" onClick={() => setShowModal(true)} />
							<StyledTextArea
								label="Rule"
								id="ruleTextAria"
								value={ruleValue}
								onChange={(e) => {
									setRuleValue(e.target.value);
								}}
							/>
							<StyledButton type="primary" text="Apply" size="small" onClick={onSubmitRule} />
							<FormHelperText error={true} hidden={isValid}>
								{errors.length !== 0 ? errors : invalidJsonMessage}
							</FormHelperText>
						</StyledDiv>
					</AccordionItem>
				</Accordion>
			</Wrapper>
			<ShowRuleModal showModal={showModal} setShowModal={setShowModal} />
		</>
	);
};

const RuleEditorRenderer = RuleEditor;
export const RuleEditorRendererRegistration = {
	tester: rankWith(100, scopeEndsWith("rule")),
	renderer: withJsonFormsControlProps(RuleEditorRenderer),
};
